import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { RotateCcw } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { useSelector } from 'react-redux';
import CommonLoader from '../CommonLoader';
import { useTranslation } from 'react-i18next';
import { doctorCurrencySymbol } from '../../utils/currency';

const EarnYearGraph = ({
  specialtyId,
  addressTypeName,
  monthWiseEarning,
  loading,
  selectedYear,
  onYearChange,
  embedded,
} = {}) => {
  const { t } = useTranslation('dashboard');
  const { selectedDoctor } = useSelector((state) => state.doctor);

  // The doctor's currency, from their country's currency code (utils/currency.js).
  const getCurrencySymbol = () => doctorCurrencySymbol(selectedDoctor);

  // Generate years based on doctor creation date
  const getYearRange = () => {
    const currentYear = new Date().getFullYear();
    if (!selectedDoctor?.createdAt) {
      return [currentYear];
    }
    const createdYear = new Date(selectedDoctor.createdAt).getFullYear();
    const yearCount = currentYear - createdYear + 1;
    return Array.from({ length: yearCount }, (_, i) => createdYear + i);
  };

  const years = getYearRange();

  // Helper function to abbreviate month names
  const abbreviateMonth = (monthName) => {
    return monthName.substring(0, 3);
  };

  // Get current year data from API
  const getYearData = () => {
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth(); // 0-11
    const createdYear = selectedDoctor?.createdAt ? new Date(selectedDoctor.createdAt).getFullYear() : currentYear;
    const createdMonth = selectedDoctor?.createdAt ? new Date(selectedDoctor.createdAt).getMonth() : 0; // 0-11

    // Determine the starting month for the selected year
    let startMonth = 0; // January
    if (selectedYear === createdYear) {
      startMonth = createdMonth;
    }

    // Determine the ending month for the selected year
    let endMonth = 11; // December
    if (selectedYear === currentYear) {
      endMonth = currentMonth;
    }

    const monthAbbreviations = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    
    if (monthWiseEarning?.data) {
      // Create a map of month names to amounts
      const monthMap = {};
      monthWiseEarning.data.forEach((item) => {
        monthMap[item.monthName] = item.totalEarnings;
      });

      // Generate only valid months (from startMonth to endMonth)
      const monthlyData = [];
      for (let i = startMonth; i <= endMonth; i++) {
        const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                           'July', 'August', 'September', 'October', 'November', 'December'];
        const monthName = monthNames[i];
        
        monthlyData.push({ 
          month: monthAbbreviations[i], 
          amount: monthMap[monthName] || 0,
          color: '#4ABDBD'
        });
      }

      return {
        total: monthWiseEarning.totalEarnings || 0,
        monthlyData: monthlyData,
        startMonth,
        endMonth
      };
    }

    // Default empty data only for valid months
    const monthlyData = [];
    for (let i = startMonth; i <= endMonth; i++) {
      monthlyData.push({ 
        month: monthAbbreviations[i], 
        amount: 0, 
        color: '#4ABDBD' 
      });
    }
    
    return {
      total: 0,
      monthlyData: monthlyData,
      startMonth,
      endMonth
    };
  };

  const currentYearData = getYearData();

  const chartData = currentYearData.monthlyData.map(item => ({
    month: item.month,
    amount: item.amount,
  }));

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const value = payload[0].value;
      const currencySymbol = getCurrencySymbol();
      return (
        <div data-testid="earn-year-graph-tooltip" className="bg-white p-2 border border-gray-200 rounded shadow-lg">
          <p data-testid="earn-year-graph-tooltip-label" className="font-semibold text-gray-900">{label}</p>
          <p data-testid="earn-year-graph-tooltip-amount" className="text-sm text-gray-700">{currencySymbol} {value.toLocaleString()}</p>
        </div>
      );
    }
    return null;
  };

  const formatYAxis = (value) => {
    const currencySymbol = getCurrencySymbol();
    if (value >= 1000000) {
      return `${currencySymbol} ${(value / 1000000).toFixed(1)}M`;
    } else if (value >= 1000) {
      return `${currencySymbol} ${(value / 1000).toFixed(1)}k`;
    }
    return `${currencySymbol} ${value}`;
  };

  const handleResetFilters = () => {
    onYearChange(new Date().getFullYear());
  };

  return (
    <motion.div
      data-testid="earn-year-graph"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.4 }}
      className={embedded ? "w-full" : "bg-white w-full rounded-xl p-4 border border-gray-200/50 shadow-md"}
    >
      <div data-testid="earn-year-graph-header" className={embedded ? "hidden" : "flex flex-col items-start gap-3 mb-4"}>
        <h3 data-testid="earn-year-graph-title" className="text-base sm:text-lg font-bold text-gray-900 flex items-center">
          <span data-testid="currency-icon" className="text-lg sm:text-xl mr-2 text-[#4ABDBD] font-bold">
            {getCurrencySymbol()}
          </span>
          {t('dashboard.monthwise_earnings')}
        </h3>
        {/* <div data-testid="earn-year-graph-controls" className="flex flex-col gap-3 w-full">
          <div className="flex gap-2 w-full">
            <select
              data-testid="earn-year-graph-year-select"
              value={selectedYear}
              onChange={(e) => onYearChange(parseInt(e.target.value))}
              className="tt-select text-xs sm:text-sm text-gray-700 rounded-lg px-3 py-2 border border-gray-200/50 focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]/20 w-40 shadow-sm"
            >
              {years.map(year => (
                <option key={year} data-testid={`earn-year-graph-year-option-${year}`} value={year}>{year}</option>
              ))}
            </select>
            <motion.button
              data-testid="earn-year-graph-reset-button"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleResetFilters}
              className="flex items-center justify-center text-xs sm:text-sm font-semibold bg-gradient-to-r from-gray-100 to-gray-200 text-gray-700 rounded-lg px-3 py-2 border border-gray-200/50 hover:from-[#4ABDBD]/10 hover:to-[#3A9B9B]/10 hover:text-[#4ABDBD] w-fit shadow-sm transition-all duration-300"
            >
              <RotateCcw data-testid="earn-year-graph-reset-icon" className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
              {t('dashboard.reset')}
            </motion.button>
          </div>
        </div> */}
      </div>

      <AnimatePresence mode="wait">
        {loading ? (
          <div data-testid="earn-year-graph-loading" className="flex flex-col items-center justify-center py-12">
            <CommonLoader />
            <span data-testid="earn-year-graph-loading-text" className="text-sm text-gray-600 mt-2">{t('dashboard.loading_earnings_data')}</span>
          </div>
        ) : (
          <motion.div
            key={selectedYear}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
            className="space-y-4"
          >
            <div data-testid="earn-year-graph-stats" className="flex items-center justify-between p-4 bg-gradient-to-r from-[#4ABDBD]/5 to-[#3A9B9B]/5 rounded-lg">
              <div data-testid="earn-year-graph-total">
                <p data-testid="earn-year-graph-total-label" className="text-sm font-medium text-gray-600">{t('dashboard.total_earnings_year', { year: selectedYear })}</p>
                <p data-testid="earn-year-graph-total-value" className="text-2xl font-bold text-gray-900">
                  {getCurrencySymbol()} {currentYearData.total.toLocaleString()}
                </p>
              </div>
              <div data-testid="earn-year-graph-average" className="text-right">
                <p data-testid="earn-year-graph-average-label" className="text-sm font-medium text-gray-600">{t('dashboard.average_monthly')}</p>
                <p data-testid="earn-year-graph-average-value" className="text-lg font-semibold text-[#4ABDBD]">
                  {getCurrencySymbol()} {Math.round(currentYearData.total / 12).toLocaleString()}
                </p>
              </div>
            </div>

            {currentYearData.total > 0 ? (
              <div data-testid="earn-year-graph-chart" className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="0" stroke="transparent" />
                    <XAxis 
                      dataKey="month" 
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: '#6B7280', fontSize: 12 }}
                    />
                    <YAxis 
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: '#6B7280', fontSize: 12 }}
                      tickFormatter={formatYAxis}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Line 
                      type="monotone" 
                      dataKey="amount" 
                      stroke="#4ABDBD" 
                      strokeWidth={3}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div data-testid="earn-year-graph-empty" className="h-80 flex flex-col items-center justify-center">
                <span data-testid="earn-year-graph-empty-currency" className="text-5xl text-gray-300 mb-2 font-bold">{getCurrencySymbol()}</span>
                <p data-testid="earn-year-graph-empty-text" className="text-sm text-gray-600">{t('dashboard.no_earnings_for_year', { year: selectedYear })}</p>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default EarnYearGraph;
